import * as Api from '@ViewModels';
import React from 'react';
import { toast } from 'sonner';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useGetSystemJobQuery } from '../../../../../queries';
import { getSystemJobProgressFingerprint, isSystemJobFinished } from '../../../../../queries/SystemJob/jobStatus';
import { useJobStallTimeout } from '../../../../../queries/SystemJob/useJobStallTimeout';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	type BrandingSystemJobResult,
	IMAGE_BRANDING_ASSISTANT_NAME,
	requestImageBrandingStream,
} from './imageBrandingStream';

type Params = {
	impersonationContext?: Api.IImpersonationContext;
	templateId?: string;
};

export function getBrandingProgressText({
	completedImages,
	currentImageAttempt,
	currentImageNumber,
	currentPhase,
	hasJob,
	isFinished,
	totalImages,
}: {
	completedImages: number;
	currentImageAttempt?: number;
	currentImageNumber?: number;
	currentPhase?: string;
	hasJob?: boolean;
	isFinished?: boolean;
	totalImages: number;
}) {
	if (!currentPhase && !hasJob) {
		return 'Starting…';
	}
	if (totalImages <= 0) {
		return '';
	}
	if (!currentPhase && isFinished) {
		return '';
	}
	if (currentPhase) {
		const imageNumber = Math.min(currentImageNumber ?? 1, totalImages);
		const attempt = currentImageAttempt ?? 1;
		const verb = currentPhase === 'Reviewing' ? 'Reviewing' : attempt > 1 ? 'Refining' : 'Generating';
		const attemptSuffix = attempt > 1 ? ` (attempt ${attempt})` : '';
		return `${verb} image ${imageNumber} of ${totalImages}…${attemptSuffix}`;
	}
	return `Branding image ${Math.min(completedImages + 1, totalImages)} of ${totalImages}…`;
}

export const useApplyBrandingToImages = ({ impersonationContext, templateId }: Params) => {
	const logger = useEventLogging('BulkRebrandingTab');

	const userSession = useUserSession();
	const [systemJobId, setSystemJobId] = React.useState<string | undefined>(undefined);
	// Number of images the current run was asked to brand. Gives a stable total to display
	// before the system job's first poll (which carries metadata.totalImages) arrives.
	const [requestedImageCount, setRequestedImageCount] = React.useState(0);

	const conversationIdRef = React.useRef<string | undefined>(undefined);
	const impersonatedAccountId = impersonationContext?.account?.id;
	const impersonatedUserId = impersonationContext?.user?.id;
	React.useEffect(() => {
		conversationIdRef.current = undefined;
	}, [impersonatedAccountId, impersonatedUserId]);

	const createConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError(`useImageAssistantConversation-error`, error);
			toast.error('Failed to create conversation');
		},
	});
	const { mutateAsync: createConversation } = createConversationMutation;

	const conversationPromiseRef = React.useRef<Promise<string | undefined> | null>(null);
	const ensureConversation = () => {
		if (conversationIdRef.current) {
			return Promise.resolve(conversationIdRef.current);
		}
		conversationPromiseRef.current ??= createConversation({
			assistantName: IMAGE_BRANDING_ASSISTANT_NAME,
			impersonationContext,
		})
			.then(conversation => {
				conversationIdRef.current = conversation?.id;
				return conversation?.id;
			})
			.finally(() => {
				conversationPromiseRef.current = null;
			});
		return conversationPromiseRef.current;
	};

	const [jobAbandoned, setJobAbandoned] = React.useState(false);

	const systemJobQuery = useGetSystemJobQuery<BrandingSystemJobResult>({
		enabled: Boolean(systemJobId),
		jobId: systemJobId,
		refetchInterval: query => {
			if (!systemJobId || jobAbandoned) {
				return false;
			}
			return isSystemJobFinished(query.state.data) ? false : 3000;
		},
		refetchOnWindowFocus: false,
	});

	const isJobOver = isSystemJobFinished(systemJobQuery.data);
	React.useEffect(() => {
		if (!systemJobId) {
			setJobAbandoned(false);
		}
	}, [systemJobId]);
	useJobStallTimeout({
		enabled: !!systemJobId && !isJobOver,
		fingerprint: `${systemJobId}:${getSystemJobProgressFingerprint(systemJobQuery.data)}`,
		onStalled: () => setJobAbandoned(true),
	});

	const startedJobRef = React.useRef(false);
	const [streamEndedWithoutJob, setStreamEndedWithoutJob] = React.useState(false);

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({ attachmentIds }: { attachmentIds: string[] }) => {
			const conversationId = conversationIdRef.current;
			if (!conversationId) {
				return Promise.reject(new Error('No image assistant conversation'));
			}
			return requestImageBrandingStream({
				userSession,
				conversationId,
				impersonationContext,
				attachmentIds,
				templateId,
			});
		},
		onError: error => {
			logger.logApiError('applyBrandingToImages-stream-error', Api.asApiError(error));
			toast.error('Image branding failed. Please try again.');
		},
		onEvent: event => {
			if (event.event !== 'ToolResponse') {
				return;
			}
			let data: { toolCall?: { systemJobId?: string } };
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}

			const toolCall = data?.toolCall;

			if (toolCall?.systemJobId) {
				startedJobRef.current = true;
				setSystemJobId(toolCall.systemJobId);
			}
		},
		onSuccess: () => {
			if (startedJobRef.current) {
				return;
			}
			setStreamEndedWithoutJob(true);
			logger.logEvent('applyBrandingToImages-no-system-job');
			toast.error('Image branding did not start. Please try again.');
		},
	});

	const runIdRef = React.useRef(0);

	const applyBranding = async ({ attachmentIds }: { attachmentIds: string[] }) => {
		const runId = ++runIdRef.current;
		startedJobRef.current = false;
		setStreamEndedWithoutJob(false);
		setSystemJobId(undefined);
		setRequestedImageCount(attachmentIds.length);
		try {
			await ensureConversation();
		} catch {
			return;
		}
		if (runIdRef.current !== runId) {
			return;
		}
		aiPromptMutation.mutate({ attachmentIds });
	};

	const cancel = () => {
		++runIdRef.current;
		aiPromptMutation.reset();
		startedJobRef.current = false;
		setStreamEndedWithoutJob(false);
		setSystemJobId(undefined);
		setRequestedImageCount(0);
	};

	const metadata = systemJobQuery.data?.metadata;
	const totalImages = metadata?.totalImages || requestedImageCount || 0;
	const completedImages = (metadata?.successfulImages ?? 0) + (metadata?.failedImages ?? 0);
	const currentPhase = metadata?.currentPhase;
	const currentImageNumber = metadata?.currentImageNumber;
	const currentImageAttempt = metadata?.currentImageAttempt;
	const systemJobStatus = systemJobQuery.data?.status;

	const progressText = React.useMemo(
		() =>
			getBrandingProgressText({
				completedImages,
				currentImageAttempt,
				currentImageNumber,
				currentPhase,
				hasJob: !!systemJobStatus,
				isFinished: isJobOver,
				totalImages,
			}),
		[completedImages, currentImageAttempt, currentImageNumber, currentPhase, isJobOver, systemJobStatus, totalImages]
	);

	const job = systemJobQuery.data;
	const isJobRunning = Boolean(systemJobId) && !jobAbandoned && !systemJobQuery.error && !isJobOver;
	const finishedEmpty = isJobOver && !job?.resultImages?.length && !job?.metadata?.failedImages;

	return {
		applyBranding,
		cancel,
		isGenerating:
			createConversationMutation.isPending ||
			aiPromptMutation.isLoading ||
			aiPromptMutation.isStreaming ||
			isJobRunning,
		resultImages: systemJobQuery.data?.resultImages,
		didFail:
			Boolean(systemJobQuery.error) ||
			aiPromptMutation.isError ||
			streamEndedWithoutJob ||
			jobAbandoned ||
			finishedEmpty,
		error: systemJobQuery.error,
		failedImages: systemJobQuery.data?.metadata?.failedImages || 0,
		totalImages,
		completedImages,
		currentPhase,
		currentImageNumber,
		currentImageAttempt,
		progressText,
		systemJobId,
		systemJobStatus,
	};
};
